// Generated by Copilot- Start - 2026-07-14
/**
 * Server-safe title/channel → package matching (WAT-11211).
 *
 * Extracted verbatim from MovieModalV2/hooks.ts so the SAME join runs in the
 * catalog proxy (server/routes/catalog.ts) and in the Movies/TV Shows modal.
 * Pure functions only — no React, no cache, no I/O. The client hook
 * (usePackageMatching) memoizes matchShowPackages; the catalog proxy calls it
 * per detail response.
 */
import type { ChannelAvailability, ChannelCardV2, PackageReference } from "./types";

/** Minimal structural view of a DTVCE show/program payload — only the fields the join reads. */
export interface MatchableChannelRef {
  callSign?: string;
  networkName?: string;
  name?: string;
}

export interface MatchableShow {
  resourceType?: string;
  networkAttributionName?: string;
  consumables?: Array<{
    provider?: { networkName?: string };
    channel?: MatchableChannelRef;
  }>;
  provider?: { networkName?: string };
  channel?: MatchableChannelRef;
  seasons?: Array<{
    contents?: Array<{
      provider?: { networkName?: string };
      channel?: MatchableChannelRef;
    }>;
  }>;
}

export interface PackageMatchResult {
  signaturePackages: PackageReference[];
  genrePackages: PackageReference[];
  packageNetworkMap: Record<string, string[]>;
  /** The exact channel.label values from channelMap that matched the show's network names.
   *  e.g. ["Cinemáx"] — used to slice svodPromoMap entries for the movie modal. */
  matchedChannelLabels: string[];
}

export const emptyPackageMatch = (): PackageMatchResult => ({
  signaturePackages: [],
  genrePackages: [],
  packageNetworkMap: {},
  matchedChannelLabels: []
});

/**
 * Strips diacritics/accents and lowercases for robust matching.
 * "Cinemáx" → "cinemax", "CINEMAX" → "cinemax", "TBS" → "tbs"
 */
export const normalizeForMatch = (s: string): string => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

// Availability precedence, matching channels.ts (included > addon > promo).
const AVAILABILITY_RANK: Record<ChannelAvailability, number> = {
  included: 3,
  addon: 2,
  promo: 1
};
const rankOf = (ref: PackageReference): number => AVAILABILITY_RANK[ref.availability] ?? 0;

/**
 * Collect a package ref into the per-id accumulator (the union across matched
 * channels), keeping the highest-availability ref when the same package arrives via
 * more than one channel — so the modal's list + legal text don't depend on
 * channelMap iteration order (WAT-11007 #7-DTVI).
 */
const addPackageRef = (byId: Map<string, PackageReference>, ref: PackageReference): void => {
  if (!ref?.id) {
    return;
  }
  const current = byId.get(ref.id);
  if (!current || rankOf(ref) > rankOf(current)) {
    byId.set(ref.id, ref);
  }
};

/**
 * Extract network names from show data.
 * Returns a Map of normalized-network-name → original-cased-name so that:
 *   - lookups against normalized channelMap labels always work (handles accents, case)
 *   - the original casing can be recovered for display ("Watch on CINEMAX")
 */
const extractNetworkNames = (show: MatchableShow): Map<string, string> => {
  // key = normalized (no accents, lowercase), value = original cased name from API
  const networkNames = new Map<string, string>();

  const add = (name: string) => {
    if (name) {
      networkNames.set(normalizeForMatch(name), name.trim());
    }
  };

  // networkAttributionName
  if (show?.networkAttributionName) {
    add(show.networkAttributionName);
  }

  // consumables — API sends mixed case e.g. "CINEMAX", "HBO Max"
  if (show?.consumables && Array.isArray(show.consumables)) {
    show.consumables.forEach(item => {
      const networkName =
        item?.provider?.networkName ||
        item?.channel?.networkName ||
        item?.channel?.callSign ||
        item?.channel?.name;
      if (networkName) {
        add(networkName);
      }
    });
  }

  // For SERIES: root-level channel/provider
  if (show?.resourceType === "SERIES") {
    const seriesNetworkName =
      show?.provider?.networkName || show?.channel?.networkName || show?.channel?.name;
    if (seriesNetworkName) {
      add(seriesNetworkName);
    }

    // seasons[0].contents[0]
    if (show?.seasons?.[0]?.contents?.[0]) {
      const episode = show.seasons[0].contents[0];
      const episodeNetwork =
        episode?.provider?.networkName ||
        episode?.channel?.networkName ||
        episode?.channel?.name;
      if (episodeNetwork) {
        add(episodeNetwork);
      }
    }
  }

  return networkNames;
};

/**
 * Map the show's channel callSigns → display network name. callSign is the STABLE
 * join between the OV show and the channels-service feed: channel *names* drift by
 * resolution (OV "TNT" vs feed "TNT HD"), but the callSign ("TNTHD") is identical on
 * both, so it matches where a name match can't (WAT-11007 #7-DTVI).
 */
const extractChannelCallSigns = (show: MatchableShow): Map<string, string> => {
  const byCallSign = new Map<string, string>();
  const add = (ch?: MatchableChannelRef) => {
    if (ch?.callSign) {
      byCallSign.set(ch.callSign.toUpperCase(), ch.networkName || ch.name || ch.callSign);
    }
  };
  show?.consumables?.forEach(item => add(item?.channel));
  add(show?.channel);
  if (show?.resourceType === "SERIES") {
    add(show?.seasons?.[0]?.contents?.[0]?.channel);
  }
  return byCallSign;
};

/**
 * Match a show/movie against the channelMap and return the UNION of the matched
 * channels' packages. Three join keys per channel, in priority order (WAT-12617):
 *   1. the card's `ovNetworkName` (OV's own network name, sent by channels-service)
 *      equals one of the show's network names — e.g. card FNCHD "Fox News Channel"
 *      carries ovNetworkName "Fox News", which is what the title names
 *   2. callSign (stable across OV and the channels-service feed)
 *   3. exact normalized channel label
 * Every card that matches is merged in: several cards can share one ovNetworkName
 * (e.g. the six HBO feeds all carry "HBO Max"), and the title lists the union of their
 * packages, deduped by id with the strongest availability kept.
 * Empty inputs → empty result, never a throw (WAT-11211 AC3).
 */
export const matchShowPackages = (
  show: MatchableShow | null | undefined,
  channelMap: Record<string, ChannelCardV2> | null | undefined
): PackageMatchResult => {
  if (!show) {
    return emptyPackageMatch();
  }
  if (!channelMap || Object.keys(channelMap).length === 0) {
    return emptyPackageMatch();
  }

  // networkNamesMap: lowercase → original-cased (e.g. "cinemax" → "CINEMAX")
  const networkNamesMap = extractNetworkNames(show);
  // callSign is the stable join — try it even when the show exposes no usable names.
  const callSignToNetwork = extractChannelCallSigns(show);
  if (networkNamesMap.size === 0 && callSignToNetwork.size === 0) {
    return emptyPackageMatch();
  }

  // Match network names with channelMap
  const packageToNetworkMap: Record<string, string[]> = {};
  // Accumulate package refs across EVERY matched channel, deduped by id. A show on
  // multiple networks (e.g. a movie on TBS + Cinemax) belongs to the UNION of those
  // channels' packages; assigning per-channel kept only the LAST match, making the
  // result depend on channelMap iteration order — the WAT-11007 #7-DTVI stage/prod gap.
  // addPackageRef builds the union of matched channels' packages, deduped by id with
  // the strongest availability kept — making the result channelMap-order-independent.
  const signatureById = new Map<string, PackageReference>();
  const genreById = new Map<string, PackageReference>();
  // Track the exact channel.label values that matched (e.g. "Cinemáx")
  // so MovieModalV2 can slice the svodPromoMap to find per-package promo text.
  const matchedChannelLabels: string[] = [];

  Object.values(channelMap).forEach(channel => {
    // Normalize the channel label the same way we normalized network names:
    // strips accents (á→a) + lowercase, so "Cinemáx" → "cinemax" matches "CINEMAX" → "cinemax"
    const channelLabel = normalizeForMatch(channel?.label ?? "");
    const ovNetworkName = normalizeForMatch(channel?.ovNetworkName ?? "");

    // matchedDisplayName: what we show in "Watch on <X>" — prefer the original
    // API network name (e.g. "TNT") so the display matches exactly what prod shows.
    let matchedDisplayName: string | null = null;

    // 1) ovNetworkName join — channels-service sends OV's own network name for the
    // channel; the expected contract is that it equals the title's network name.
    // 2) callSign join — runs whenever the ovNetworkName join misses (no ovNetworkName on
    // the card, or the title names the network differently). Channel names drift by
    // resolution ("TNT" vs "TNT HD"), but the callSign ("TNTHD") is identical on both feeds.
    // 3) Exact name match — normalized channel label is one of the show's network names.
    const cardCallSign = channel?.callSign?.toUpperCase();
    if (ovNetworkName && networkNamesMap.has(ovNetworkName)) {
      matchedDisplayName = networkNamesMap.get(ovNetworkName) ?? channel?.label;
    } else if (cardCallSign && callSignToNetwork.has(cardCallSign)) {
      matchedDisplayName = callSignToNetwork.get(cardCallSign) || channel?.label;
    } else if (channelLabel && networkNamesMap.has(channelLabel)) {
      matchedDisplayName = networkNamesMap.get(channelLabel) || channel?.label;
    }

    if (matchedDisplayName !== null) {
      // Track the original channel.label for svodPromoMap lookup
      if (!matchedChannelLabels.includes(channel.label)) {
        matchedChannelLabels.push(channel.label);
      }

      // Accumulate this channel's packages into the deduped union (returned to the modal)
      const channelSignaturePackages = channel.signaturePackages || [];
      const channelGenrePackages = channel.genrePackages || [];
      channelSignaturePackages.forEach(ref => addPackageRef(signatureById, ref));
      channelGenrePackages.forEach(ref => addPackageRef(genreById, ref));
      // package-to-network mapping uses THIS channel's packages → the matched network
      const allPackageIds: PackageReference[] = [...channelSignaturePackages, ...channelGenrePackages];

      allPackageIds.forEach(pkgId => {
        const pkgIdStr = String(pkgId?.id);
        if (!packageToNetworkMap[pkgIdStr]) {
          packageToNetworkMap[pkgIdStr] = [];
        }
        if (!packageToNetworkMap[pkgIdStr].includes(matchedDisplayName!)) {
          packageToNetworkMap[pkgIdStr].push(matchedDisplayName!);
        }
      });
    }
  });

  // Union across all matched channels (deduped by id) — the full package set the
  // show belongs to, independent of channelMap iteration order.
  return {
    signaturePackages: Array.from(signatureById.values()),
    genrePackages: Array.from(genreById.values()),
    packageNetworkMap: packageToNetworkMap,
    matchedChannelLabels
  };
};

/**
 * Resolve a single searched channel (by callSign, OV network name or name) to its
 * channelMap card — the card already carries signaturePackages/genrePackages
 * (WAT-11211 AC1). An exact callSign is the most specific identity a caller can send,
 * so it wins here; then ovNetworkName (WAT-12617), then the exact label. Any ovNetworkName
 * match beats a label match elsewhere. Several cards can share one ovNetworkName (HD/SD,
 * regional feeds): among those, the one whose label equals the identity wins, else the first.
 */
export const matchChannelByIdentity = (
  identity: string,
  channelMap: Record<string, ChannelCardV2> | null | undefined
): ChannelCardV2 | null => {
  if (!identity || !channelMap) {
    return null;
  }
  const upper = identity.toUpperCase();
  const normalized = normalizeForMatch(identity);
  const cards = Object.values(channelMap);
  const byLabel = (list: ChannelCardV2[]) => list.find(card => normalizeForMatch(card?.label ?? "") === normalized);
  const byCallSign = cards.find(card => card?.callSign?.toUpperCase() === upper);
  if (byCallSign) {
    return byCallSign;
  }
  const byOvName = cards.filter(card => card?.ovNetworkName && normalizeForMatch(card.ovNetworkName) === normalized);
  if (byOvName.length > 0) {
    return byLabel(byOvName) ?? byOvName?.[0];
  }
  return byLabel(cards) ?? null;
};
// Generated by Copilot- End - 2026-07-14
